/**
 * Reading a pasted loom register.
 *
 * Two shapes arrive, because two are pasted:
 *
 *  - the plant's own loom production register — L.NO, MODEL, PARTY, NAME DAY,
 *    12 HOUR, NAME NIGHT, 12 HOUR, TOTAL … — whose second column is the
 *    loom's make, not a person, and whose metres belong to whichever of the
 *    two shifts is being entered;
 *  - the roll register this screen asks for — loom, operator, party, input kg,
 *    output kg, roll number, metres.
 *
 * A header row names the columns and settles it. Without one, a second column
 * holding a loom model gives the production register away. Lines for the same
 * loom become one row with several rolls. A name that is not in the masters is
 * reported and left blank rather than throwing the whole paste away — the
 * register is the plant's, written before this system existed, and half of it
 * is worth more than none of it. Looms that stood still are left off.
 */

import type { Shift } from "../../lib/types";

export interface PastedRoll {
  roll_no: string;
  metres: string;
}

export interface PastedLoom {
  loom_no: string;
  operator_id: string;
  party_id: string;
  output_kg: string;
  rolls: PastedRoll[];
}

export interface PasteMasters {
  operators: { id: number; code: string; name: string }[];
  parties: { id: number; code: string; name: string }[];
  looms: { loom_no: number; party_id: number | null; model: string | null }[];
}

export interface PasteResult {
  looms: PastedLoom[];
  /**
   * Any input kilograms the paste carried, added up. Tape is issued to the
   * floor rather than to a loom, so a column of it belongs to the shift, not
   * to the rows.
   */
  inputKg: string;
  /** Looms on the paste with nothing on them — no name, no metres, no kg. */
  idle: number;
  unknownOperators: string[];
  unknownParties: string[];
  /**
   * Lines that are not loom rows: the party-wise totals, averages and stray
   * cells the sheet carries under the ninety looms, and any loom number the
   * floor does not have. They are counted and passed over, never a reason to
   * refuse the rest — people paste the whole sheet, not a tidy block of it.
   */
  ignored: number;
}

/** Where each thing sits on a line, once the layout is known. */
interface Layout {
  loom: number;
  party: number | null;
  operator: number | null;
  metres: number | null;
  inputKg: number | null;
  outputKg: number | null;
  rollNo: number | null;
}

/** Excel gives tabs; a copied report may give commas or aligned spaces. */
export function cellsOf(line: string): string[] {
  if (line.includes("\t")) return line.split("\t");
  if (line.includes(",")) return line.split(",");
  const wide = line.trim().split(/\s{2,}/);
  return wide.length > 1 ? wide : line.trim().split(/\s+/);
}

/** Empty, a dash or "n/a" all mean nothing was written in that box. */
function blank(cell: string): boolean {
  const v = cell.trim().toLowerCase();
  return v === "" || v === "-" || v === "--" || v === "–" || v === "n/a" || v === "na";
}

const cellAt = (cells: string[], at: number | null) =>
  at === null ? "" : (cells[at] ?? "").trim();

/** The number in a box, or "" if there is not one. */
function numeric(raw: string): string {
  const v = raw.replace(/[, ]/g, "");
  return v !== "" && Number.isFinite(Number(v)) ? v : "";
}

const isLoomNo = (cell: string) => /^\d+$/.test(cell.trim());

/** A header row, read column by column. Null when the line is not one. */
function layoutFromHeader(cells: string[], forShift: Shift): Layout | null {
  const head = cells.map((c) => c.trim().toLowerCase().replace(/\./g, " "));
  if (!head.some((h) => /^l\s*no\b|^loom/.test(h))) return null;
  const out: Layout = {
    loom: 0, party: null, operator: null, metres: null,
    inputKg: null, outputKg: null, rollNo: null,
  };
  // Day columns come before night ones and the metres follow the name whose
  // shift they belong to, so the side is carried along the row.
  let side: Shift | null = null;
  head.forEach((h, at) => {
    if (h === "") return;
    if (/^l\s*no\b|^loom/.test(h)) { out.loom = at; return; }
    if (/model|make/.test(h)) return;
    if (/party|customer/.test(h)) { out.party = at; return; }
    if (/total|difference|capacity|efficien/.test(h)) { side = null; return; }
    if (/night/.test(h)) {
      side = "NIGHT";
      if (forShift === "NIGHT") out.operator = at;
      return;
    }
    if (/\bday\b/.test(h)) {
      side = "DAY";
      if (forShift === "DAY") out.operator = at;
      return;
    }
    if (/roll/.test(h)) { out.rollNo = at; return; }
    if (/in\s*kg|input/.test(h)) { out.inputKg = at; return; }
    if (/out\s*kg|output/.test(h)) { out.outputKg = at; return; }
    if (/name|operator|weaver/.test(h)) { out.operator = at; return; }
    if (/hour|mtr|met(re|er)|production|qty/.test(h)) {
      if (side === null || side === forShift) out.metres = at;
    }
  });
  return out;
}

/** The roll register this screen documents, when nothing says otherwise. */
const ROLL_REGISTER: Layout = {
  loom: 0, party: 2, operator: 1, metres: 6, inputKg: 3, outputKg: 4, rollNo: 5,
};

const productionRegister = (forShift: Shift): Layout => ({
  loom: 0, party: 2,
  operator: forShift === "NIGHT" ? 5 : 3,
  metres: forShift === "NIGHT" ? 6 : 4,
  inputKg: null, outputKg: null, rollNo: null,
});

export function parseLoomPaste(
  text: string,
  shift: Shift,
  masters: PasteMasters
): PasteResult {
  const byName = <T extends { id: number; code: string; name: string }>(
    list: T[], raw: string
  ) =>
    list.find((x) => x.name.toLowerCase() === raw.trim().toLowerCase() ||
                     x.code.toLowerCase() === raw.trim().toLowerCase());
  const models = new Set(
    masters.looms.map((l) => (l.model ?? "").trim().toLowerCase()).filter(Boolean)
  );

  const lines = text.split(/\r?\n/).map(cellsOf)
    .filter((cells) => cells.some((c) => c.trim() !== ""));

  // The layout: from a header row if the paste has one, otherwise from a
  // second column that holds a loom model.
  let layout: Layout | null = null;
  let first = lines.length;
  for (const [i, cells] of lines.entries()) {
    if (isLoomNo(cells[0] ?? "")) { first = i; break; }
    layout = layoutFromHeader(cells, shift) ?? layout;
  }
  if (!layout || layout.operator === null) {
    const sample = lines.slice(first, first + 5);
    const isProduction = sample.some((c) => models.has((c[1] ?? "").trim().toLowerCase()));
    layout = isProduction ? productionRegister(shift) : layout ?? ROLL_REGISTER;
  }

  const merged = new Map<string, PastedLoom>();
  const unknownOperators = new Set<string>();
  const unknownParties = new Set<string>();
  const onFloor = new Set(masters.looms.map((l) => String(l.loom_no)));
  let idle = 0;
  let ignored = 0;
  let inputTotal = 0;

  for (const [i, cells] of lines.entries()) {
    if (i < first) continue;
    const loomNo = cellAt(cells, layout.loom);
    if (!isLoomNo(loomNo) || (onFloor.size > 0 && !onFloor.has(loomNo))) {
      ignored += 1;
      continue;
    }
    const operatorRaw = cellAt(cells, layout.operator);
    const partyRaw = cellAt(cells, layout.party);
    const inputKg = numeric(cellAt(cells, layout.inputKg));
    const outputKg = numeric(cellAt(cells, layout.outputKg));
    const rollCell = cellAt(cells, layout.rollNo);
    const rollNo = blank(rollCell) ? "" : rollCell;
    const metres = numeric(cellAt(cells, layout.metres));

    const ran = !blank(operatorRaw) || Number(inputKg || 0) > 0 ||
                Number(outputKg || 0) > 0 || Number(metres || 0) > 0 || rollNo !== "";
    if (!ran) { idle += 1; continue; } // a loom that stood still

    const loom: PastedLoom = merged.get(loomNo) ?? {
      loom_no: loomNo, operator_id: "", party_id: "", output_kg: "", rolls: [],
    };
    if (!blank(operatorRaw)) {
      const operator = byName(masters.operators, operatorRaw);
      if (operator) loom.operator_id = String(operator.id);
      else unknownOperators.add(operatorRaw.trim());
    }
    if (!blank(partyRaw)) {
      const party = byName(masters.parties, partyRaw);
      if (party) loom.party_id = String(party.id);
      else unknownParties.add(partyRaw.trim());
    }
    if (inputKg) inputTotal += Number(inputKg);
    if (outputKg) loom.output_kg = outputKg;
    if (rollNo || Number(metres || 0) > 0) loom.rolls.push({ roll_no: rollNo, metres });
    merged.set(loomNo, loom);
  }

  return {
    looms: [...merged.values()],
    inputKg: inputTotal ? String(Number(inputTotal.toFixed(3))) : "",
    idle,
    unknownOperators: [...unknownOperators],
    unknownParties: [...unknownParties],
    ignored,
  };
}

/** What the paste did, in a line the floor can read. */
export function pasteSummary(result: PasteResult): string {
  const names = (list: string[], what: string) =>
    list.length
      ? `no ${what} named ${list.slice(0, 3).map((n) => `"${n}"`).join(", ")}` +
        `${list.length > 3 ? ` and ${list.length - 3} more` : ""} — left blank`
      : "";
  const n = result.looms.length;
  return [
    `${n} ${n === 1 ? "loom" : "looms"} added`,
    result.idle ? `${result.idle} that stood still left off` : "",
    result.ignored ? `${result.ignored} lines that are not loom rows passed over` : "",
    result.inputKg ? `${result.inputKg} kg in, put against the whole shift` : "",
    names(result.unknownOperators, "operator"),
    names(result.unknownParties, "party"),
  ].filter(Boolean).join(" · ") + ".";
}
